/**
 * LoginForm component.
 *
 * Renders the email + password form for the /login page.
 * The parent (LoginPage) controls navigation and actor-type branching;
 * this component is purely a controlled form with error display.
 *
 * Accessibility:
 * - label elements associated with inputs via htmlFor/id
 * - inline error region with aria-live="polite" and role="alert"
 * - submit button disables and shows loading state during in-flight request
 * - autofocus on email input (OK on /login where login is the user's intent)
 *
 * Design spec compliance:
 * - Deep blue-black surface1 background card
 * - Cyan focus ring on inputs
 * - Geist sans font throughout
 * - Submit button hover lift (translateY -1px)
 * - No em dashes anywhere in copy
 *
 * Error message copy:
 * - invalid_credentials: "Invalid email or password."
 * - rate_limited: "Too many attempts. Try again in Nmin." (N computed from retry_after)
 * - generic: "Something went wrong. Please try again."
 */
import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { LoginError } from './schemas';
import {
  loginWrapper,
  loginCard,
  loginMascot,
  loginHeading,
  loginSubheading,
  fieldGroup,
  fieldLabel,
  fieldInput,
  errorRegion,
  submitButton,
  resetPrompt,
  resetLink,
} from './LoginForm.css';

/** Props for the LoginForm component. */
interface LoginFormProps {
  /**
   * Called when the form is submitted with valid field values.
   * Should call useAuth().login() and handle the returned promise.
   * The form handles loading state and error display internally.
   *
   * @param email - Email address entered by the user
   * @param password - Password entered by the user
   */
  onSubmit: (email: string, password: string) => Promise<void>;
}

/**
 * Converts a retry_after_seconds number to a human-readable "Nmin" or "Ns" string.
 *
 * @param seconds - Number of seconds until retry is allowed
 * @returns Display string, e.g. "5 min" or "45s"
 */
function formatRetryAfter(seconds: number): string {
  if (seconds >= 60) {
    const mins = Math.ceil(seconds / 60);
    return `${mins} min`;
  }
  return `${seconds}s`;
}

/**
 * Derives the inline error message string from a caught error.
 *
 * @param err - Error thrown by onSubmit (may be LoginError or generic Error)
 * @returns Human-readable error message for display
 */
function getErrorMessage(err: unknown): string {
  if (err instanceof LoginError) {
    if (err.code === 'invalid_credentials') {
      return 'Invalid email or password.';
    }
    if (err.code === 'rate_limited') {
      const after = err.retry_after != null ? ` Try again in ${formatRetryAfter(err.retry_after)}.` : '';
      return `Too many attempts.${after}`;
    }
  }
  return 'Something went wrong. Please try again.';
}

/**
 * LoginForm - email + password login form.
 *
 * Parent is responsible for navigation after a successful login.
 * This component surfaces loading state and typed error messages.
 */
export function LoginForm({ onSubmit }: LoginFormProps) {
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Reset-help disclosure state (2026-06-10): replaces the dead anchor.
  const [showResetHelp, setShowResetHelp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * Handles form submission.
   * Validates fields are present (HTML5 required does most of the work),
   * sets loading state, calls onSubmit, handles errors inline.
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email || !password) return;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      await onSubmit(email, password);
      // On success, parent handles navigation. We don't reset state
      // because the component will unmount or be replaced.
    } catch (err: unknown) {
      setErrorMessage(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className={loginWrapper}>
      <div className={loginCard}>
        <img
          src="/mutemigit.png"
          alt="SignalSentry mascot"
          className={loginMascot}
          width={192}
          height={192}
          draggable={false}
        />
        <h1 className={loginHeading}>Sign in to SignalSentry</h1>
        <p className={loginSubheading}>Client portal access</p>

        <form
          onSubmit={handleSubmit}
          aria-label="Log in"
          noValidate={false}
        >
          {/* Identifier field (email OR staff username — fleet-api accepts both) */}
          <div className={fieldGroup}>
            <label htmlFor={emailId} className={fieldLabel}>
              Email or username
            </label>
            <input
              id={emailId}
              type="text"
              name="identifier"
              autoComplete="username"
              required
              autoFocus
              placeholder="you@example.com or sigsentry"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={fieldInput}
              aria-describedby={errorMessage ? errorId : undefined}
              disabled={isLoading}
            />
          </div>

          {/* Password field */}
          <div className={fieldGroup}>
            <label htmlFor={passwordId} className={fieldLabel}>
              Password
            </label>
            <input
              id={passwordId}
              type="password"
              name="password"
              autoComplete="current-password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={fieldInput}
              aria-describedby={errorMessage ? errorId : undefined}
              disabled={isLoading}
            />
          </div>

          {/*
           * Inline error region.
           * aria-live="polite" announces errors to screen readers after they appear.
           * role="alert" makes the region an assertive live region when content is present.
           * The region is always rendered (even when empty) so screen readers track it.
           */}
          <div
            id={errorId}
            className={errorRegion}
            aria-live="polite"
            role="alert"
            aria-atomic="true"
          >
            {errorMessage}
          </div>

          {/* Submit button */}
          {/* Phase 20 MOB-09: the 40.25px Log in submit button is just
              below the 44px floor; widening would touch the carefully-tuned
              card layout from Phase 3 ADR-002. Annotated for the
              mobile-smoke audit per Plan 20-08; bump to 44/48 deferred to
              a future plan that owns the auth-card design. */}
          <button
            type="submit"
            disabled={isLoading}
            aria-disabled={isLoading}
            data-state={isLoading ? 'loading' : 'idle'}
            className={submitButton}
            data-tap-target-ignore
          >
            {isLoading ? 'Signing in...' : 'Log in'}
          </button>
        </form>

        {/*
         * Password reset prompt (reworked 2026-06-10, doc D issue 9).
         * The old markup was a permanently dead `href="#"` anchor with
         * aria-disabled + tabIndex=-1: a visible affordance that could
         * never do anything. There is still no self-service reset flow
         * (user management is an API/DB exercise), so the honest fix is a
         * real, keyboard-reachable disclosure that explains the actual
         * reset path instead of a link that silently no-ops.
         */}
        <p className={resetPrompt}>
          Forgot your password?{' '}
          <button
            type="button"
            className={resetLink}
            aria-expanded={showResetHelp}
            onClick={() => setShowResetHelp((v) => !v)}
            data-testid="login-reset-help-toggle"
            data-tap-target-ignore
            style={{
              // Render the button in the existing link idiom: the resetLink
              // class supplies color/underline; these strip the UA button
              // chrome only (layout-only inline style, not theming).
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              cursor: 'pointer',
            }}
          >
            How do I reset it?
          </button>
        </p>
        {showResetHelp ? (
          <p
            className={resetPrompt}
            role="note"
            data-testid="login-reset-help-text"
          >
            Password resets are handled by your SignalSentry administrator.
            Contact your account admin; they can reset it from the fleet
            console host.
          </p>
        ) : null}
      </div>
    </main>
  );
}
